/** @jsxImportSource @emotion/react */
'use memo'

import type { ReactNode } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphXSmall, weightMedium } from 'src/components/css'

interface EntityActionButtonProps {
  label: string
  icon: ReactNode
  onClick: () => void
  variant?: 'primary' | 'outlined'
  disabled?: boolean
  'aria-label'?: string
}

/**
 * Shared entity-action button (Edit / Add / Family Management, etc.) used
 * across feature headers — e.g. the profile header and the operations panel —
 * so the treatment can't drift between call sites.
 */
const EntityActionButton = ({
  label,
  icon,
  onClick,
  variant = 'outlined',
  disabled = false,
  'aria-label': ariaLabel,
}: EntityActionButtonProps) => {
  'use memo'

  return (
    <button
      type='button'
      css={variant === 'primary' ? primaryButtonStyle : outlinedButtonStyle}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {icon}
      {capitalizeWords(label)}
    </button>
  )
}

export default EntityActionButton

const baseButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  /* Explicit height so sibling controls that are not this component — the
     Operations panel's move select and its icon buttons — can line up with it
     exactly rather than approximating the padding. */
  height: 34px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  ${paragraphXSmall}
  ${weightMedium}
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  .anticon {
    font-size: 16px;
  }
`

const primaryButtonStyle = css`
  ${baseButtonStyle}
  background-color: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: none;
`

const outlinedButtonStyle = css`
  ${baseButtonStyle}
  background-color: transparent;
  color: ${colors.NEUTRAL_0};
  border: 1px solid ${colors.PRIMARY};
`
